import React from "react";
import {AppStore} from "./AppStore";
import {DisposeUtils} from "../utils/DisposeUtils";
import {ErrorUtils} from "../utils/ErrorUtils";
import {proto} from "../proto/messages";
import {makeAutoObservable, runInAction} from "mobx";
import {lessons} from "../proto/compiled";

export const LessonStoreContext = React.createContext<LessonStore | undefined>(undefined);

export class LessonStore {
  private disposer = new DisposeUtils();
  private app: AppStore;
  private lessonId: string;
  private loadPending = false;
  loading = false;
  lesson?: lessons.ILessonDetail;
  activeChapterId?: string;

  constructor(app: AppStore, lessonId: string) {
    this.app = app;
    this.lessonId = lessonId;
    makeAutoObservable(this);
    this.load();
  }

  get activeChapter(): lessons.ILessonChapter | undefined {
    if (!this.lesson?.chapters) return undefined;
    return this.lesson.chapters.find(c => c.id === this.activeChapterId)
      || this.lesson.chapters[0];
  }

  get statistics() {
    if (!this.lesson) return null;
    return {
      chapters: this.lesson.chapterCount ?? 0,
      videoExamples: this.lesson.videoExampleCount ?? 0,
      durationMinutes: Math.round((this.lesson.totalDurationSeconds ?? 0) / 60),
    };
  }

  setActiveChapter(chapterId: string) {
    this.activeChapterId = chapterId;
  }

  dispose() {
    this.disposer.dispose();
  }

  async load() {
    if (!this.lessonId) {
      return;
    }
    if (this.loading) {
      this.loadPending = true;
      return;
    }
    this.loading = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      const response = await this.app.socketApi.fetch(
        proto.TxGetLesson.create({id: this.lessonId}),
        new proto.RxGetLessonResponse(),
      );
      runInAction(() => {
        this.lesson = response.proto?.lesson ?? undefined;
        // Set first chapter as active if not already set
        if (!this.activeChapterId && this.lesson?.chapters?.length) {
          this.activeChapterId = this.lesson.chapters[0].id ?? undefined;
        }
      });
    } catch (e) {
      ErrorUtils.fromFetch(e);
    }
    runInAction(() => {
      this.loading = false;
      if (this.loadPending) {
        this.loadPending = false;
        this.load();
      }
    });
  }
}
